import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import ResearchLayout from '../../components/ResearchLayout';
import api from '../../services/api';
import { Link } from 'react-router-dom';
import moment from 'moment';
import Modal from '../../components/Modal';

// Helper function to format seconds to "0hrs 0mins 0secs" format
const formatTime = (seconds) => {
  if (!seconds || seconds <= 0) return '0hrs 0mins 0secs';
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return `${hrs}hrs ${mins}mins ${secs}secs`;
};

const ViewReportsPage = () => {
  const { user } = useAuth();
  const isMedicalAdmin = user && (user.role === 'medical_admin' || (user.role && user.role.toLowerCase().includes('medical_admin')));
  const isAdminOrProgram = user && (
    (typeof user.role === 'string' && (user.role === 'medical_admin' || user.role === 'program_admin')) ||
    (user.role && user.role.toLowerCase && (user.role.toLowerCase().includes('medical_admin') || user.role.toLowerCase().includes('program_admin')))
  );
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredReports, setFilteredReports] = useState([]);
  const [assignedDoctors, setAssignedDoctors] = useState([]);
  const [selectedDoctorId, setSelectedDoctorId] = useState(null);
  const [doctorProgress, setDoctorProgress] = useState(null);
  const [doctorProgressError, setDoctorProgressError] = useState(null);
  const [loadingDoctors, setLoadingDoctors] = useState(false);
  const [filters, setFilters] = useState({
    reportType: '',
    dateFrom: '',
    dateTo: '',
  });
  const [showProtocolProgress, setShowProtocolProgress] = useState(true);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailedProgressData, setDetailedProgressData] = useState(null);
  const [loadingDetailedProgress, setLoadingDetailedProgress] = useState(false);
  const [doctorFilter, setDoctorFilter] = useState('active'); // 'all', 'active', 'inactive'

  useEffect(() => {
    loadReports();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [reports, searchTerm, filters]);

  // Load doctors assigned by this medical admin so we can show progress even when no reports
  useEffect(() => {
    const loadAssignedDoctors = async () => {
      if (!user) return;
      setLoadingDoctors(true);
      try {
        let url = '/assignments/details';
        const role = typeof user.role === 'string' ? user.role.toLowerCase() : (user.role?.name || '').toLowerCase();
        if (role === 'medical_admin' || role.includes('medical_admin')) {
          url = `/assignments/details?assigned_by=${user.id}&include_inactive=true`;
        }
        const resp = await api.get(url);
        const assignments = resp.data?.assignments || resp.data || [];
        let map = {};
        assignments.forEach(a => {
          const docId = a.doctor_id || a.doctorId || (a.doctor && a.doctor.id);
          const docName = a.doctor_name || (a.doctor && a.doctor.name) || `Doctor #${docId}`;
          const docIsActive = a.is_active !== false;
          const docEmail = a.doctor_email || a.email || '';
          if (docId) map[docId] = { id: docId, name: docName, is_active: docIsActive, email: docEmail };
        });
        let docs = Object.values(map);
        
        // Apply doctor status filter
        if (doctorFilter === 'active') {
          docs = docs.filter(d => d.is_active !== false && !d.email?.startsWith('deleted_') && !d.email?.startsWith('delete_delete_'));
        } else if (doctorFilter === 'inactive') {
          docs = docs.filter(d => d.is_active === false || d.email?.startsWith('deleted_') || d.email?.startsWith('delete_delete_'));
        }
        
        setAssignedDoctors(docs);
        if (docs.length > 0) {
          setSelectedDoctorId(docs[0].id);
        } else {
          setSelectedDoctorId(null);
        }
      } catch (err) {
        console.error('Failed loading assigned doctors', err);
      } finally {
        setLoadingDoctors(false);
      }
    };

    loadAssignedDoctors();
  }, [user, doctorFilter]);

  useEffect(() => {
    if (selectedDoctorId) {
      loadDoctorProgress(selectedDoctorId);
    } else {
      setDoctorProgress(null);
    }
  }, [selectedDoctorId]);

  const loadReports = async () => {
    setLoading(true);
    try {
      const response = await api.get('/reports');
      console.log('Reports:', response.data);
      if (response.data && response.data.success) {
        setReports(response.data.reports);
        setFilteredReports(response.data.reports);
      } else {
        setError('Failed to load reports');
      }
    } catch (err) {
      console.error('Error loading reports:', err);
      setError('Failed to load reports. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...reports];

    // Search term filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(report => 
        report.title.toLowerCase().includes(term) || 
        report.description.toLowerCase().includes(term)
      );
    }

    // Report type filter
    if (filters.reportType) {
      filtered = filtered.filter(report => 
        report.report_type.toLowerCase() === filters.reportType.toLowerCase()
      );
    }

    // Date filters
    if (filters.dateFrom) {
      const fromDate = new Date(filters.dateFrom);
      filtered = filtered.filter(report => 
        new Date(report.created_at) >= fromDate
      );
    }

    if (filters.dateTo) {
      const toDate = new Date(filters.dateTo);
      toDate.setHours(23, 59, 59, 999); // End of day
      filtered = filtered.filter(report => 
        new Date(report.created_at) <= toDate
      );
    }

    setFilteredReports(filtered);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const resetFilters = () => {
    setFilters({
      reportType: '',
      dateFrom: '',
      dateTo: '',
    });
    setSearchTerm('');
  };

  const loadDoctorProgress = async (doctorId) => {
    if (!doctorId) return;
    try {
      setDoctorProgressError(null);
      const resp = await api.get(`/reports/doctor-progress?doctor_id=${doctorId}`);
      if (resp.data && resp.data.success) {
        setDoctorProgress(resp.data);
      } else {
        setDoctorProgress(null);
        setDoctorProgressError(resp.data?.error || 'No progress data');
      }
    } catch (err) {
      console.error('Failed to load doctor progress', err);
      setDoctorProgress(null);
      setDoctorProgressError(err.response?.data?.message || err.message || 'Request failed');
    }
  };

  const loadDetailedDoctorProgress = async (doctorId) => {
    if (!doctorId) return;
    setLoadingDetailedProgress(true);
    try {
      const response = await api.get(`/reports/doctor-detailed-progress-v3?doctor_id=${doctorId}`);
      if (response.data && response.data.success !== false) {
        const data = response.data;
        
        // Calculate total_cases_answered from batches - use cases_started (any answers) not completed_cases
        if (data && data.batches && Array.isArray(data.batches)) {
          const totalCasesAnswered = data.batches.reduce((sum, batch) => {
            return sum + (batch.cases_started || batch.completed_cases || 0);
          }, 0);
          
          // Update the overall_stats with calculated value
          if (data.overall_stats) {
            data.overall_stats.total_cases_answered = totalCasesAnswered;
          }
        }
        
        setDetailedProgressData(data);
      } else {
        console.error('Detailed progress error:', response.data?.error || 'Unknown error');
        setDetailedProgressData({
          error: response.data?.error || 'Failed to load detailed progress',
          message: response.data?.message || 'Unable to load detailed progress information for this doctor.'
        });
      }
    } catch (err) {
      console.error('Error loading detailed progress:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Network error';
      setDetailedProgressData({
        error: 'Failed to load detailed progress',
        message: errorMessage
      });
    } finally {
      setLoadingDetailedProgress(false);
    }
  };

  const handleShowMoreDetails = () => {
    if (selectedDoctorId) {
      setShowDetailsModal(true);
      loadDetailedDoctorProgress(selectedDoctorId);
    }
  };

  const getCompletionColor = (percentage) => {
    if (percentage >= 80) return 'success';
    if (percentage >= 60) return 'warning';
    if (percentage >= 40) return 'info';
    return 'danger';
  };

  const getCompletionIcon = (percentage) => {
    if (percentage >= 100) return 'fas fa-check-circle';
    if (percentage >= 80) return 'fas fa-thumbs-up';
    if (percentage >= 60) return 'fas fa-clock';
    if (percentage >= 40) return 'fas fa-hourglass-half';
    return 'fas fa-exclamation-triangle';
  };

  return (
    <ResearchLayout pageTitle="View Reports">
      <div className="container-fluid">
        {/* Header */}
        <div className="row mb-4">
          <div className="col">
            <div className="card border-0 shadow-sm bg-gradient-info text-white">
              <div className="card-body" style={{backgroundColor: '#7B68EE'}}>
                <h3 className="mb-2">Statistical Reports</h3>
                <p className="mb-0 opacity-75">
                  View research insights and data analysis prepared by statisticians
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <div className="row align-items-center mb-3">
                  <div className="col-md-4 mb-3 mb-md-0">
                    <div className="input-group">
                      <span className="input-group-text bg-transparent border-end-0">
                        <i className="fas fa-search text-muted"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control border-start-0"
                        placeholder="Search reports..."
                        value={searchTerm}
                        onChange={handleSearch}
                      />
                    </div>
                  </div>
                  <div className="col-md-4 mb-3 mb-md-0">
                    <select
                      className="form-select"
                      value={doctorFilter}
                      onChange={(e) => setDoctorFilter(e.target.value)}
                    >
                      <option value="all">All Doctors</option>
                      <option value="active">Active Only</option>
                      <option value="inactive">Inactive Only</option>
                    </select>
                  </div>
                  <div className="col-md-4 text-md-end">
                    <button 
                      className="btn btn-outline-secondary" 
                      type="button" 
                      data-bs-toggle="collapse" 
                      data-bs-target="#filterCollapse"
                    >
                      <i className="fas fa-filter me-2"></i>
                      Filters
                    </button>
                  </div>
                </div>
                
                <div className="collapse mt-3" id="filterCollapse">
                  <div className="card card-body border">
                    <div className="row">
                      <div className="col-md-3 mb-3">
                        <label className="form-label">Doctor Status</label>
                        <select
                          className="form-select"
                          value={doctorFilter}
                          onChange={(e) => setDoctorFilter(e.target.value)}
                        >
                          <option value="all">All Doctors</option>
                          <option value="active">Active Only</option>
                          <option value="inactive">Inactive Only</option>
                        </select>
                      </div>
                      <div className="col-md-3 mb-3">
                        <label className="form-label">Report Type</label>
                        <select
                          className="form-select"
                          name="reportType"
                          value={filters.reportType}
                          onChange={handleFilterChange}
                        >
                          <option value="">All Types</option>
                          <option value="statistical">Statistical</option>
                          <option value="research">Research</option>
                          <option value="clinical">Clinical</option>
                          <option value="demographic">Demographic</option>
                        </select>
                      </div>
                      <div className="col-md-4 mb-3">
                        <label className="form-label">From Date</label>
                        <input
                          type="date"
                          className="form-control"
                          name="dateFrom"
                          value={filters.dateFrom}
                          onChange={handleFilterChange}
                        />
                      </div>
                      <div className="col-md-4 mb-3">
                        <label className="form-label">To Date</label>
                        <input
                          type="date"
                          className="form-control"
                          name="dateTo"
                          value={filters.dateTo}
                          onChange={handleFilterChange}
                        />
                      </div>
                      <div className="col-12">
                        <button className="btn btn-secondary" onClick={resetFilters}>
                          <i className="fas fa-undo me-2"></i>
                          Reset Filters
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Reports Grid */}
        <div className="row">
          {loading ? (
            <div className="col-12 text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : error ? (
            <div className="col-12">
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            </div>
          ) : filteredReports.length === 0 ? (
            <>
              {isMedicalAdmin && (
                <div className="col-12 mb-4">
                  <div className="card border-0 shadow-sm">
                    <div className="card-body">
                      <h5 className="mb-3">Assigned Doctor Progress</h5>
                      {/* Doctor Status Filter Dropdown */}
                      <div className="mb-3">
                        <div className="row align-items-center">
                          <div className="col-md-3">
                            <label className="form-label mb-0">Doctor Status</label>
                          </div>
                          <div className="col-md-3">
                            <select
                              className="form-select form-select-sm"
                              value={doctorFilter}
                              onChange={(e) => setDoctorFilter(e.target.value)}
                            >
                              <option value="all">All Doctors</option>
                              <option value="active">Active Only</option>
                              <option value="inactive">Inactive Only</option>
                            </select>
                          </div>
                        </div>
                      </div>
                      {loadingDoctors ? (
                        <div className="text-center py-3">Loading doctors...</div>
                      ) : assignedDoctors.length === 0 ? (
                        <div className="alert alert-secondary">No doctors assigned yet.</div>
                      ) : (
                        <div className="row align-items-center">
                          <div className="col-md-4 mb-3">
                            <select className="form-select" value={selectedDoctorId || ''} onChange={e => setSelectedDoctorId(parseInt(e.target.value, 10))}>
                              {assignedDoctors.map(d => (
                                <option key={d.id} value={d.id}>{d.name || `Dr ${d.id}`}</option>
                              ))}
                            </select>
                          </div>
                          <div className="col-md-8">
                            {doctorProgress ? (
                              <div>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                  <div className="d-flex gap-3 align-items-center">
                                    <div className="me-3">
                                      <i className="fas fa-user-md fa-2x text-primary"></i>
                                    </div>
                                    <div>
                                      <div className="small text-muted">Doctor</div>
                                      <div className="fw-bold">{doctorProgress.doctor_name || assignedDoctors.find(d => d.id === selectedDoctorId)?.name || `Doctor #${doctorProgress.doctor_id}`}</div>
                                    </div>
                                  </div>
                                  <button
                                    className="btn btn-outline-primary btn-sm"
                                    onClick={handleShowMoreDetails}
                                  >
                                    <i className="fas fa-info-circle me-1"></i>
                                    Show More Details
                                  </button>
                                </div>

                                <div className="d-flex gap-3 align-items-center mb-3">
                                  <div className="me-3"><i className="fas fa-clock fa-2x text-muted"></i></div>
                                  <div>
                                    <div className="small text-muted">Time Taken</div>
                                    <div className="fw-bold">{formatTime(doctorProgress.elapsed_seconds_estimate || 0)}</div>
                                  </div>
                                  <div className="ms-4"><i className="fas fa-check-circle fa-2x text-success"></i></div>
                                  <div>
                                    <div className="small text-muted">Answered</div>
                                    <div className="fw-bold">{doctorProgress.total_answered_questions || 0} qs</div>
                                  </div>
                                </div>

                                {(() => {
                                  // Calculate progress based on questions assigned vs answered
                                  const totalAssigned = doctorProgress.total_assigned_questions || 0;
                                  const totalAnswered = doctorProgress.total_answered_questions || 0;
                                  const questionProgress = totalAssigned > 0 ? Math.round((totalAnswered / totalAssigned) * 100) : 0;
                                  
                                  return (
                                    <>
                                      <div className="mb-2 d-flex justify-content-between align-items-center">
                                        <div className="small text-muted">Progress</div>
                                        <div className="small text-muted">{questionProgress}%</div>
                                      </div>
                                      <div className="progress" role="progressbar" aria-label="Doctor progress" aria-valuenow={questionProgress} aria-valuemin="0" aria-valuemax="100">
                                        <div className="progress-bar bg-warning" style={{ width: `${questionProgress}%` }} />
                                      </div>
                                    </>
                                  );
                                })()}
                                  {/* Per-protocol smaller progress bars */}
                                  {doctorProgress.assignments && doctorProgress.assignments.length > 0 && (
                                    (() => {
                                      const map = {};
                                      doctorProgress.assignments.forEach(a => {
                                        const key = a.protocol_id || `p-${a.protocol_name || 'unknown'}`;
                                        if (!map[key]) map[key] = { protocol_id: a.protocol_id, protocol_name: a.protocol_name || `Protocol ${a.protocol_id}`, assigned_seconds: 0, answered_questions: 0 };
                                        map[key].assigned_seconds += (a.assigned_duration_seconds || 0);
                                        map[key].answered_questions += (a.answered_question_count || 0);
                                      });
                                      const protocols = Object.values(map);
                                      return (
                                        <div className="mt-3">
                                          <div className="d-flex justify-content-between align-items-center mb-2">
                                            <div className="small text-muted">Protocol progress</div>
                                            <button
                                              type="button"
                                              className="btn btn-sm btn-link text-decoration-none"
                                              onClick={() => setShowProtocolProgress(s => !s)}
                                              aria-expanded={showProtocolProgress}
                                            >
                                              {showProtocolProgress ? (
                                                <><i className="fas fa-chevron-up"></i> Hide</>
                                              ) : (
                                                <><i className="fas fa-chevron-down"></i> Show</>
                                              )}
                                            </button>
                                          </div>
                                          <div className={`collapse ${showProtocolProgress ? 'show' : ''}`}>
                                            <div className="d-flex flex-column gap-2">
                                            {protocols.map(p => {
                                              // Use the backend percent_complete directly to match the detailed modal
                                              const pct = p.percent_complete || 0;
                                              return (
                                                <div key={p.protocol_id} className="d-flex align-items-center">
                                                  <div className="me-2" style={{width: '160px'}}>
                                                    <small className="fw-semibold">{p.protocol_name}</small>
                                                  </div>
                                                  <div className="flex-grow-1">
                                                    <div className="progress" role="progressbar" aria-valuenow={pct} aria-valuemin="0" aria-valuemax="100" style={{height: '10px'}}>
                                                      <div className="progress-bar bg-info" style={{width: `${pct}%`}} />
                                                    </div>
                                                  </div>
                                                  <div className="ms-3" style={{width: '50px'}}>
                                                    <small className="text-muted">{pct}%</small>
                                                  </div>
                                                </div>
                                              );
                                            })}
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })()
                                  )}
                              </div>
                            ) : (
                              <div className="card border-0 shadow-sm">
                                <div className="card-body text-center py-5">
                                  <i className="fas fa-file-alt fa-3x text-muted mb-3"></i>
                                  <h5>No reports found</h5>
                                  <p className="mb-0 text-muted">No progress reports exist for the selected doctor. Assign protocols or wait for the doctor to begin answering questions.</p>
                                </div>
                              </div>
                            )}
                            {doctorProgressError && (
                              <div className="mt-3 alert alert-warning">Error loading progress: {doctorProgressError}</div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="col-12">
                <div className="card border-0 shadow-sm">
                  <div className="card-body text-center py-5">
                    <i className="fas fa-file-alt fa-3x text-muted mb-3"></i>
                    <h5>No reports found</h5>
                    <p className="mb-0 text-muted">
                      No statistical reports are available. Check back later or adjust your filters.
                    </p>
                  </div>
                </div>
              </div>
            </>
          ) : (
            filteredReports.map(report => (
              <div key={report.id} className="col-md-6 col-lg-4 mb-4">
                <div className="card h-100 border-0 shadow-sm">
                  <div className="card-header bg-light d-flex justify-content-between align-items-center">
                    <span className={`badge ${
                      report.report_type === 'statistical' ? 'bg-primary' : 
                      report.report_type === 'research' ? 'bg-success' : 
                      report.report_type === 'clinical' ? 'bg-warning' : 
                      'bg-info'
                    }`}>
                      {report.report_type}
                    </span>
                    <small className="text-muted">
                      {moment(report.created_at).format('MMM D, YYYY')}
                    </small>
                  </div>
                  <div className="card-body">
                    <h5 className="card-title">{report.title}</h5>
                    <p className="card-text text-muted mb-3">
                      {report.description.length > 120 ? 
                        `${report.description.substring(0, 120)}...` : 
                        report.description}
                    </p>
                    <div className="d-flex justify-content-between align-items-center">
                      <small className="text-muted">
                        <i className="fas fa-user me-1"></i>
                        {report.created_by_name || 'Statistician'}
                      </small>
                      <span className="badge bg-light text-dark">
                        {report.data_points || '0'} data points
                      </span>
                    </div>
                  </div>
                  <div className="card-footer bg-white border-0">
                    <Link 
                      to={`/medical-admin/reports/${report.id}`} 
                      className="btn btn-outline-primary d-block"
                    >
                      <i className="fas fa-chart-bar me-2"></i>
                      View Report
                    </Link>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Doctor Progress Panel */}
  {isMedicalAdmin && assignedDoctors.length > 0 && (
          <div className="row mt-4">
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-light d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">Assigned Doctors Progress</h5>
                  <div className="btn-group">
                    {assignedDoctors.map(doctor => (
                      <button
                        key={doctor.id}
                        className={`btn btn-outline-primary ${selectedDoctorId === doctor.id ? 'active' : ''}`}
                        onClick={() => setSelectedDoctorId(doctor.id)}
                      >
                        {doctor.name}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="card-body">
                  {loadingDoctors ? (
                    <div className="text-center py-3">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : doctorProgress ? (
                    <div>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="mb-0">Progress for {assignedDoctors.find(doc => doc.id === selectedDoctorId)?.name}</h6>
                        <button
                          className="btn btn-outline-primary btn-sm"
                          onClick={handleShowMoreDetails}
                        >
                          <i className="fas fa-info-circle me-1"></i>
                          Show More Details
                        </button>
                      </div>
                      <div className="row">
                        <div className="col-md-4 mb-3">
                          <div className="p-3 border rounded-3 bg-light">
                            <h6 className="mb-2">Total Reports</h6>
                            <h3 className="fw-bold">{doctorProgress.total_reports}</h3>
                          </div>
                        </div>
                        <div className="col-md-4 mb-3">
                          <div className="p-3 border rounded-3 bg-light">
                            <h6 className="mb-2">Completed Reports</h6>
                            <h3 className="fw-bold">{doctorProgress.completed_reports}</h3>
                          </div>
                        </div>
                        <div className="col-md-4 mb-3">
                          <div className="p-3 border rounded-3 bg-light">
                            <h6 className="mb-2">Pending Reports</h6>
                            <h3 className="fw-bold">{doctorProgress.pending_reports}</h3>
                          </div>
                        </div>
                      </div>
                      <Link 
                        to={`/medical-admin/reports/doctor/${selectedDoctorId}`} 
                        className="btn btn-primary"
                      >
                        <i className="fas fa-arrow-right me-2"></i>
                        View Detailed Progress
                      </Link>
                    </div>
                  ) : (
                    <div className="text-center py-3">
                      <i className="fas fa-chart-line fa-3x text-muted mb-3"></i>
                      <h6 className="mb-0">No progress data available</h6>
                      <p className="mb-0 text-muted">
                        The selected doctor has no reports yet. Assign reports to track progress.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Detailed Progress Modal */}
        <Modal
          isOpen={showDetailsModal}
          onClose={() => setShowDetailsModal(false)}
          title={`Detailed Progress Report - ${assignedDoctors.find(d => d.id === selectedDoctorId)?.name || 'Doctor'}`}
        >
          <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            {loadingDetailedProgress ? (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading detailed progress...</p>
              </div>
            ) : detailedProgressData ? (
              <div>
                {/* Overall Statistics */}
                <div className="mb-4">
                  <h6 className="mb-3">Overall Statistics</h6>
                  <div className="row">
                    <div className="col-md-4 mb-3">
                      <div className="card border-0 shadow-sm h-100">
                        <div className="card-body text-center">
                          <div className="text-primary mb-2">
                            <i className="fas fa-clipboard-list fa-2x"></i>
                          </div>
                          <h5 className="fw-bold text-primary">{detailedProgressData.overall_stats?.total_protocols || 0}</h5>
                          <p className="text-muted mb-0 small">Total Protocols</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="col-md-4 mb-3">
                      <div className="card border-0 shadow-sm h-100">
                        <div className="card-body text-center">
                          <div className="text-success mb-2">
                            <i className="fas fa-users fa-2x"></i>
                          </div>
                          <h5 className="fw-bold text-success">
                            {detailedProgressData.overall_stats?.total_cases_answered || 0}/{detailedProgressData.overall_stats?.total_cases || 0}
                          </h5>
                          <p className="text-muted mb-0 small">Cases Answered</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="col-md-4 mb-3">
                      <div className="card border-0 shadow-sm h-100">
                        <div className="card-body text-center">
                          <div className="text-info mb-2">
                            <i className="fas fa-question-circle fa-2x"></i>
                          </div>
                          <h5 className="fw-bold text-info">
                            {detailedProgressData.overall_stats?.total_questions_answered || 0}/{detailedProgressData.overall_stats?.total_questions_available || 0}
                          </h5>
                          <p className="text-muted mb-0 small">Questions Answered</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="col-md-4 mb-3">
                      <div className="card border-0 shadow-sm h-100">
                        <div className="card-body text-center">
                          <div className="text-warning mb-2">
                            <i className="fas fa-clock fa-2x"></i>
                          </div>
                          <h5 className="fw-bold text-warning">
                            {detailedProgressData.overall_stats?.total_time_spent_minutes || 0} min
                          </h5>
                          <p className="text-muted mb-0 small">Time Taken</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="col-md-4 mb-3">
                      <div className="card border-0 shadow-sm h-100">
                        <div className="card-body text-center">
                          <div className={`text-${getCompletionColor(detailedProgressData.overall_stats?.overall_completion_percentage || 0)} mb-2`}>
                            <i className={`${getCompletionIcon(detailedProgressData.overall_stats?.overall_completion_percentage || 0)} fa-2x`}></i>
                          </div>
                          <h5 className={`fw-bold text-${getCompletionColor(detailedProgressData.overall_stats?.overall_completion_percentage || 0)}`}>
                            {detailedProgressData.overall_stats?.overall_completion_percentage || 0}%
                          </h5>
                          <p className="text-muted mb-0 small">Overall Completion</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Batch Totals Section */}
                {detailedProgressData.batch_totals && detailedProgressData.batch_totals.length > 0 && (
                  <div className="mb-4">
                    <h6 className="mb-3">Batch Totals (All Active Batches)</h6>
                    <div className="row">
                      {detailedProgressData.batch_totals.map((batch) => (
                        <div key={batch.batch_id} className="col-md-4 mb-3">
                          <div className="card border-0 shadow-sm h-100">
                            <div className="card-body text-center">
                              <h6 className="fw-bold text-primary mb-2">{batch.batch_name}</h6>
                              <div className="row">
                                <div className="col-6">
                                  <h5 className="fw-bold text-info mb-0">{batch.total_cases_in_batch}</h5>
                                  <small className="text-muted">Total Cases</small>
                                </div>
                                <div className="col-6">
                                  <h5 className="fw-bold text-success mb-0">{batch.total_questions_in_batch}</h5>
                                  <small className="text-muted">Total Questions</small>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Protocol Details */}
                {detailedProgressData.protocols && detailedProgressData.protocols.length > 0 && (
                  <div className="mb-4">
                    <h6 className="mb-3">Protocol Progress Details</h6>
                    <div className="row">
                      {detailedProgressData.protocols.map((protocol, index) => {
                        // Use the backend completion_percentage directly to match the progress bars
                        const displayPercentage = protocol.completion_percentage || 0;
                        
                        return (
                          <div key={protocol.protocol_id} className="col-12 mb-3">
                            <div className="card border">
                              <div className="card-header bg-light">
                                <div className="d-flex justify-content-between align-items-center">
                                  <h6 className="mb-0 fw-bold">{protocol.protocol_name}</h6>
                                  <span className={`badge bg-${getCompletionColor(displayPercentage)}`}>
                                    {displayPercentage}%
                                  </span>
                                </div>
                              </div>
                              <div className="card-body">
                                {protocol.protocol_description && (
                                  <p className="text-muted small mb-3">{protocol.protocol_description}</p>
                                )}
                                
                                <div className="row mb-3">
                                  <div className="col-4">
                                    <div className="text-center">
                                      <div className="h6 text-primary mb-1">{protocol.total_cases}</div>
                                      <div className="small text-muted">Total Cases</div>
                                    </div>
                                  </div>
                                  <div className="col-4">
                                    <div className="text-center">
                                      <div className="h6 text-info mb-1">{protocol.total_questions}</div>
                                      <div className="small text-muted">Questions per Protocol</div>
                                    </div>
                                  </div>
                                  <div className="col-4">
                                    <div className="text-center">
                                      <div className="h6 text-success mb-1">{protocol.answered_questions}</div>
                                      <div className="small text-muted">Answered</div>
                                    </div>
                                  </div>
                                </div>

                                <div className="progress mb-2" style={{ height: '8px' }}>
                                  <div
                                    className={`progress-bar bg-${getCompletionColor(displayPercentage)}`}
                                    role="progressbar"
                                    style={{ width: `${displayPercentage}%` }}
                                    aria-valuenow={displayPercentage}
                                    aria-valuemin="0"
                                    aria-valuemax="100"
                                  ></div>
                                </div>
                                
                                <div className="d-flex justify-content-between small text-muted">
                                  <span>
                                    {protocol.answered_questions || 0} of {protocol.total_questions || 0} questions answered
                                  </span>
                                  <span>{displayPercentage}%</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Work History */}
                {detailedProgressData.work_history && detailedProgressData.work_history.length > 0 && (
                  <div className="mb-4">
                    <h6 className="mb-3">Recent Work History (Last 30 Days)</h6>
                    <div className="row">
                      {detailedProgressData.work_history.slice(0, 8).map((day, index) => (
                        <div key={day.date} className="col-md-6 mb-3">
                          <div className="card border h-100">
                            <div className="card-body text-center">
                              <div className="text-primary mb-2">
                                <i className="fas fa-calendar-day"></i>
                              </div>
                              <h6 className="fw-bold">{new Date(day.date).toLocaleDateString()}</h6>
                              <div className="small text-muted">
                                <div><strong>{day.questions_answered}</strong> questions</div>
                                <div><strong>{day.protocols_worked_on}</strong> protocols</div>
                                <div><strong>{day.cases_worked_on}</strong> cases</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Completed Assignments */}
                {detailedProgressData.completed_assignments && detailedProgressData.completed_assignments.length > 0 && (
                  <div className="mb-4">
                    <h6 className="mb-3">Recent Completed Assignments</h6>
                    <div className="table-responsive">
                      <table className="table table-hover table-sm">
                        <thead className="table-light">
                          <tr>
                            <th>Protocol</th>
                            <th>Case ID</th>
                            <th>Questions</th>
                            <th>Completed</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detailedProgressData.completed_assignments.slice(0, 10).map((assignment, index) => (
                            <tr key={index}>
                              <td className="fw-bold small">{assignment.protocol_name}</td>
                              <td>
                                <span className="badge bg-primary">{assignment.case_id}</span>
                              </td>
                              <td>
                                <span className="badge bg-success">{assignment.questions_answered}</span>
                              </td>
                              <td className="text-muted small">
                                {assignment.completed_at ? new Date(assignment.completed_at).toLocaleDateString() : 'N/A'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            ) : detailedProgressData && detailedProgressData.error ? (
              <div className="text-center py-4">
                <i className="fas fa-exclamation-triangle fa-3x text-warning mb-3"></i>
                <h6 className="text-warning">{detailedProgressData.error}</h6>
                <p className="text-muted mb-0">{detailedProgressData.message}</p>
                <button
                  className="btn btn-outline-primary btn-sm mt-3"
                  onClick={() => loadDetailedDoctorProgress(selectedDoctorId)}
                >
                  <i className="fas fa-redo me-1"></i>
                  Retry
                </button>
              </div>
            ) : (
              <div className="text-center py-4">
                <i className="fas fa-exclamation-triangle fa-3x text-muted mb-3"></i>
                <h6 className="text-muted">No detailed progress data available</h6>
                <p className="text-muted mb-0">Unable to load detailed progress information for this doctor.</p>
                <button
                  className="btn btn-outline-primary btn-sm mt-3"
                  onClick={() => loadDetailedDoctorProgress(selectedDoctorId)}
                >
                  <i className="fas fa-redo me-1"></i>
                  Retry
                </button>
              </div>
            )}
          </div>
        </Modal>
      </div>
    </ResearchLayout>
  );
};

export default ViewReportsPage;